import { AnyRouter } from '@tanstack/react-router'
import { action, autorun, computed, makeObservable, observable, runInAction } from 'mobx'
import i18n, { changeLanguage } from 'i18next'

import {
    User,
    SystemScope,
    Language,
    availableLanguages,
    Timezone,
    TimezoneSchema,
    Organization,
    OrganizationScope,
    OrganizationPermission,
    UserOrganization,
    type ExtendedClientAppType,
} from '@campai/lib-common/entities'
import { changeI18nErrorMap } from '@campai/lib-common/utils/zod'
import {
    getOrganizationCountryProfile,
    getOrganizationAccess,
    hasOrganizationPermissions,
} from '@campai/lib-common/utils/organization'
import { hasSystemPermissions } from '@campai/lib-common/utils/system'
import { ObjectId } from '@campai/lib-common/utils/objectid'
import { setUserTimezone, userTimezone } from '@campai/lib-common/utils/date'

import { env } from '@/env'
import { trackPageViews } from '@/utils/plausible'
import { KeyValueStorage, SimpleCache, indexedDbKeyValueStorageImplementation } from '@/utils'
import { Auth, AuthSettings, getWebAuthProviderConfig, initAuth } from '@/utils/auth'
import { DebugOptionsType } from '@/app/AppDebugBar'
import { client } from '@/utils/trpc'
import { getWebPushPermission, registerWebPushServiceWorker, requestWebPushPermission } from '@/utils/webPush'

import type {
    init,
    addIntegration,
    makeFetchTransport,
    tanstackRouterBrowserTracingIntegration,
    ErrorBoundary as SentryErrorBoundary,
} from '@sentry/react'
import type { ErrorBoundary as ReactErrorBoundary } from 'react-error-boundary'
import type {
    setUser,
    setTag,
    addBreadcrumb,
    getCurrentScope,
    captureException,
    extraErrorDataIntegration,
} from '@sentry/core'

type SentryCoreType = {
    setUser: typeof setUser
    setTag: typeof setTag
    addBreadcrumb: typeof addBreadcrumb
    getCurrentScope: typeof getCurrentScope
    captureException: typeof captureException
    extraErrorDataIntegration: typeof extraErrorDataIntegration
}

type SentryReactType = {
    init: typeof init
    addIntegration: typeof addIntegration
    makeFetchTransport: typeof makeFetchTransport
    tanstackRouterBrowserTracingIntegration: typeof tanstackRouterBrowserTracingIntegration
    ErrorBoundary: typeof SentryErrorBoundary
}

export type WebAppStateInitParams = {
    forceUser: boolean
    authSettings?: Partial<AuthSettings>
    router: AnyRouter
    registerForWebPush?: boolean
}

type WebAppStateOptions = {
    useSentry?: boolean
    clientApp?: ExtendedClientAppType
    // For apps loading their translations on demand. It runs before `language` changes, so that it's always loaded
    loadLanguage?: (language: Language) => Promise<void>
}

const defaultLanguage = 'de'

export type BaseOrganization = Pick<
    Organization,
    '_id' | 'shortId' | 'address' | 'brand' | 'avatar' | 'name' | 'stripeAccount'
>

export class WebAppState<TOrganization extends BaseOrganization = BaseOrganization> {
    debugOptions = {
        logTRPCCalls: false,
        hideUpcomingFeatures: false,
        hideScrollbars: false,
    } as DebugOptionsType

    keyValueStorage: KeyValueStorage
    cache: SimpleCache

    language: Language = defaultLanguage
    auth!: Auth
    authSettings!: AuthSettings
    // @ts-expect-error will be assign on init
    user: User
    sentry: SentryCoreType | null = null
    get displayTimezone(): Timezone {
        return this.user?.timezone || userTimezone || TimezoneSchema.parse(env.VITE_SERVER_TZ)
    }

    get organizationCountryProfile() {
        return getOrganizationCountryProfile(this.organization.address.country)
    }

    // @ts-expect-error its fine it will be assigned in init
    router: AnyRouter
    // @ts-expect-error its fine it will be assigned in init
    organization: TOrganization
    sentryReactPackage?: SentryReactType
    sentryCorePackage?: SentryCoreType
    loadPromise?: Promise<void>
    onMutationSuccess?: (path: string, input: unknown) => void
    enableDebugBar: boolean
    SentryErrorBoundaryComponent?: typeof SentryErrorBoundary
    ReactErrorBoundaryComponent?: typeof ReactErrorBoundary
    webPushPermission: NotificationPermission | 'unsupported' = getWebPushPermission()
    readonly clientApp: ExtendedClientAppType | null
    readonly loadLanguage: (language: Language) => Promise<void>

    constructor({ useSentry = true, clientApp, loadLanguage = () => Promise.resolve() }: WebAppStateOptions = {}) {
        this.clientApp = clientApp ?? null
        this.loadLanguage = loadLanguage
        this.keyValueStorage = new KeyValueStorage(indexedDbKeyValueStorageImplementation)
        this.cache = new SimpleCache(this.keyValueStorage)
        this.loadPromise = this.load({ useSentry })

        const browserLanguages = window.navigator.languages.map((locale) => (locale || '').split('-')[0])
        const browserLanguage = browserLanguages.find((lang) => availableLanguages.includes(lang as Language)) || null
        this.language = (browserLanguage as null | Language) || defaultLanguage
        this.enableDebugBar = localStorage.getItem('enableDebugBar') !== 'false'

        makeObservable(this, {
            language: observable,
            auth: observable.ref,
            user: observable,
            enableDebugBar: observable,
            organization: observable,
            webPushPermission: observable,
            organizationAccess: computed,
            toggleEnableDebugBar: action,
            updateWebPushPermission: action,
        })
    }

    async load({ useSentry }: { useSentry?: boolean }) {
        if (useSentry) {
            const sentryReact = await import('@sentry/react')

            this.sentryCorePackage = await import('@sentry/core')
            this.sentryReactPackage = sentryReact

            const client = sentryReact.init({
                dsn: env.VITE_SENTRY_DSN_WEB,
                environment: this.isDevelopment ? 'development' : 'production',
                enabled: !this.isLocalhost,
                release: `campai@${env.VITE_PRODUCT_RELEASE}`,
                transport: (options) => {
                    const nativeFetch = window.fetch

                    return sentryReact.makeFetchTransport(options, async (...args) => {
                        const response = await nativeFetch(...args)

                        // Sentry only reads the status and headers, and the browser keeps every unread response alive.
                        // A session is sent per URL change, so these would pile up for as long as the page stays open
                        response.body?.cancel().catch(() => undefined)

                        return response
                    })
                },
                integrations: (integrations) => {
                    return [
                        ...integrations,
                        ...(this.sentryCorePackage
                            ? [
                                  this.sentryCorePackage.extraErrorDataIntegration({
                                      captureErrorCause: true,
                                  }),
                              ]
                            : []),
                    ]
                },
            })

            client?.on('afterSendEvent', (event) => {
                console.log('Sentry event id:', event.event_id)
            })

            // set the error boundary component
            if (this.sentryReactPackage) {
                this.SentryErrorBoundaryComponent = this.sentryReactPackage.ErrorBoundary
            }
        } else {
            const { ErrorBoundary } = await import('react-error-boundary')
            this.ReactErrorBoundaryComponent = ErrorBoundary
        }
    }

    async loadUser(forceLoad: boolean = false) {
        await this.auth.loadCreateUser(
            forceLoad,
            async (user) => {
                if (user?.language) {
                    await this.loadLanguage(user.language)
                }

                runInAction(() => {
                    setUserTimezone(user?.timezone ?? null)
                    if (user) {
                        this.user = {
                            ...user,
                            systemPermissions: user.systemPermissions || [],
                        }
                        if (this.user) {
                            this.language = this.user.language ?? this.language
                            this.sentry?.setUser({ id: this.user.userId, email: this.user.email })
                        }
                    }
                })
            },
            this.keyValueStorage
        )
    }

    async initSentry(sentry?: SentryCoreType) {
        if (sentry) {
            this.sentry = sentry

            const sentryOptions = sentry.getCurrentScope().getClient()?.getOptions()
            if (sentryOptions) {
                sentryOptions.beforeBreadcrumb = (breadcrumb) => {
                    if (
                        breadcrumb.type === 'http' &&
                        breadcrumb.category === 'fetch' &&
                        breadcrumb.data &&
                        typeof breadcrumb.data.url === 'string' &&
                        breadcrumb.data.url.startsWith(`${env.VITE_PRODUCT_ENDPOINT}/trpc`)
                    ) {
                        return null
                    }

                    return breadcrumb
                }
            }

            if (this.auth.userId) {
                this.sentry?.setUser({ id: this.auth.userId })
            }
        }
    }

    toggleEnableDebugBar = () => {
        this.enableDebugBar = !this.enableDebugBar
        localStorage.setItem('enableDebugBar', this.enableDebugBar.toString())
    }

    updateWebPushPermission = () => {
        this.webPushPermission = getWebPushPermission()
    }

    async requestWebPushPermission() {
        const permission = await requestWebPushPermission()

        runInAction(() => {
            this.webPushPermission = permission
        })

        if (permission === 'granted') {
            await registerWebPushServiceWorker()
        }

        return permission
    }

    async registerWebPushIfAllowed() {
        this.updateWebPushPermission()

        if (this.webPushPermission === 'granted') {
            await registerWebPushServiceWorker()
        }
    }

    async init({ router, forceUser, authSettings = {}, registerForWebPush }: WebAppStateInitParams) {
        if (this.loadPromise) {
            await this.loadPromise
        }

        if (env.VITE_PRODUCT_VERSION) {
            const cachedVersion = await this.keyValueStorage.get<string>('product.version')

            if (cachedVersion !== env.VITE_PRODUCT_VERSION) {
                if (!cachedVersion || cachedVersion < env.VITE_PRODUCT_VERSION) {
                    const colorScheme = await this.keyValueStorage.get('colorScheme')
                    await this.keyValueStorage.clear()

                    if (colorScheme !== undefined) {
                        await this.keyValueStorage.set('colorScheme', colorScheme)
                    }
                    await this.keyValueStorage.set('product.version', env.VITE_PRODUCT_VERSION)
                }
            }
        }

        if (this.isDevelopment) {
            const debugOptions = await this.keyValueStorage.get<DebugOptionsType>('debug.options')
            if (debugOptions) {
                this.debugOptions = debugOptions
            }
        }

        const { provider, issuer, clientId } = getWebAuthProviderConfig()
        this.authSettings = {
            issuer,
            legacyIssuer: provider === 'authy' ? env.VITE_BLUEID_ISSUER : undefined,
            clientId,
            forceUser,
            ...authSettings,
        }
        this.auth = await initAuth(this.authSettings)

        if (this.auth.userId) {
            await this.loadUser()
            if (registerForWebPush) {
                this.registerWebPushIfAllowed()
            }

            const params = new URLSearchParams(window.location.search)
            const sourceNotificationId = params.get('sourceNotificationId')
            const sourceNotificationActivityAt = new Date(params.get('sourceNotificationActivityAt') ?? '')

            if (sourceNotificationId && !Number.isNaN(sourceNotificationActivityAt.getTime())) {
                client.currentUser.notifications.markNotificationRead.mutate({
                    notificationId: sourceNotificationId,
                    through: sourceNotificationActivityAt,
                })
            }
        }

        this.router = router

        // Page tracking
        trackPageViews(window.location.pathname) // track initial page

        router.subscribe('onResolved', (e) => {
            if (e.fromLocation?.pathname !== e.toLocation.pathname) {
                trackPageViews(window.location.pathname)
            }
        })

        // add the integration with tanstack router to sentry
        this.sentryReactPackage?.addIntegration(
            this.sentryReactPackage.tanstackRouterBrowserTracingIntegration(this.router)
        )

        this.initSentry(this.sentryCorePackage)

        autorun(() => {
            if (this.hideScrollbars) {
                document.body.classList.add('hide-scrollbars')
            } else {
                document.body.classList.remove('hide-scrollbars')
            }
        })

        autorun(() => {
            const country = this.organization?.address?.country
            const locale = country ? `${this.language}-${country}` : this.language
            changeLanguage(locale)
            document.documentElement.lang = this.language
            changeI18nErrorMap(i18n)
        })
    }

    get isLocalhost() {
        return import.meta.env.DEV
    }

    get isDevelopment() {
        return env.VITE_PRODUCT_ENV !== 'production'
    }

    get hideScrollbars() {
        return this.isDevelopment && this.debugOptions.hideScrollbars
    }

    hasSystemPermissions(permissions: SystemScope[]) {
        return hasSystemPermissions(this.user?.systemPermissions, permissions)
    }

    hasSystemPermission(permission: SystemScope) {
        return this.hasSystemPermissions([permission])
    }

    async clearStorages() {
        await Promise.all([this.keyValueStorage.clear()])
    }

    get organizationAccess(): UserOrganization | null {
        if (!this.user || !this.organization) {
            return null
        }

        const organizationId = this.organization._id

        const organizationAccess =
            getOrganizationAccess({
                organizationId,
                user: this.user,
            }) || null

        return organizationAccess
    }

    hasOrganizationPermissions(
        permissions: Partial<Record<OrganizationScope, Partial<OrganizationPermission>>>,
        accessObject: ObjectId | false | undefined = undefined
    ) {
        return hasOrganizationPermissions(this.organizationAccess, permissions, accessObject)
    }

    protected setOrganization(organization: TOrganization) {
        runInAction(() => {
            this.organization = organization
            this.sentry?.setTag('organization', this.organization.shortId)
        })
    }

    activateSupport() {
        window.open('mailto:support@campai.com', '_blank')
    }
}
